const ItemType = require('@/lib/Items/ItemType');
const { getService } = require('@/lib/DI/injector');
const { toUpperSnakeCase } = require('@/functions/strings');

/**
 * Returns all the distinct item types of the lines in the order, ignoring zero quantity lines.
 *
 * The special item types of PRODUCT_FOOD and PRODUCT_KITCHEN are also included. But they behave differently.
 * - If it is a "food" type product, the PRODUCT_FOOD type is added *instead of* the PRODUCT type.
 * - If it is a "show on kitchen" product, the PRODUCT_KITCHEN type is added *in addition to* the PRODUCT type.
 * This is awkwardly different, but is actually consistent with how this works in other places such as the item tabs.
 *
 * @param {Order} order
 *
 * @return {string[]} Values from ItemType.
 */
const getOrderItemTypes = (order) => {
    const itemTypes = new Set();

    order.getLines().forEach((line) => {
        if (line.qty < 1) {
            return;
        }

        const item = line.getItem();

        if (line.getItemType() === ItemType.PRODUCT && item?.isFood) {
            itemTypes.add(ItemType.PRODUCT_FOOD);
        } else {
            itemTypes.add(line.getItemType());
        }

        if (item?.showOnKitchenScreen) {
            itemTypes.add(ItemType.PRODUCT_KITCHEN);
        }
    });

    return Array.from(itemTypes);
};

/**
 * Build the list of lang keys for a message about an order that varies depending on the item type.
 * e.g. if you provide:
 * 'ORDER_DETAILS.CONFIRM_ORDER_PROMPT'
 * and the order contains tickets and memberships it will return
 * [
 *     'ORDER_DETAILS.CONFIRM_ORDER_PROMPT--TICKET',
 *     'ORDER_DETAILS.CONFIRM_ORDER_PROMPT--MEMBERSHIP_PLAN',
 * ]
 * It also checks if those lang key exists. So if no --PRODUCT lang key exists with that prefix for example,
 * it will be skipped.
 *
 * @param {string} baseKey
 * @param {Order} order
 *
 * @return {string[]}
 */
const getLangStringKeyItemTypeVariantsForOrder = (baseKey, order) => {
    /**
     * @type {LangService}
     */
    const langService = getService('langService');

    return getOrderItemTypes(order)
        .map((itemType) => `${baseKey}--${toUpperSnakeCase(itemType)}`)
        .filter((key) => langService.hasKey(key));
};

module.exports = {
    getLangStringKeyItemTypeVariantsForOrder,
    getOrderItemTypes,
};
